import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useOrg } from "@/context/OrgContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CheckCircle, FileText, Leaf, Drop, Recycle, Wind, FileArrowDown, Tree, UsersThree, HandHeart, ShoppingCart, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import { API_BASE } from "@/lib/api";

const TABS = [
  { key: "E1", label: "E1 · İklim", icon: Leaf, group: "Çevresel" },
  { key: "E2", label: "E2 · Kirlilik", icon: Wind, group: "Çevresel" },
  { key: "E3", label: "E3 · Su", icon: Drop, group: "Çevresel" },
  { key: "E4", label: "E4 · Biyoçeşitlilik", icon: Tree, group: "Çevresel" },
  { key: "E5", label: "E5 · Döngüsel", icon: Recycle, group: "Çevresel" },
  { key: "S1", label: "S1 · İşgücü", icon: UsersThree, group: "Sosyal" },
  { key: "S2", label: "S2 · Değer Zinciri", icon: UsersThree, group: "Sosyal" },
  { key: "S3", label: "S3 · Topluluk", icon: HandHeart, group: "Sosyal" },
  { key: "S4", label: "S4 · Tüketici", icon: ShoppingCart, group: "Sosyal" },
  { key: "G1", label: "G1 · Yönetişim", icon: Buildings, group: "Yönetişim" },
];

export default function Esrs() {
  const { t } = useLang();
  const { role } = useOrg();
  const [year, setYear] = useState(new Date().getFullYear());
  const [tab, setTab] = useState("E1");
  const [e1, setE1] = useState(null);
  const [qual, setQual] = useState(null);
  const [drafts, setDrafts] = useState({});

  const loadE1 = async (y) => {
    const { data } = await api.get(`/esrs/e1/${y}`);
    setE1(data);
  };

  const loadQual = async (std, y) => {
    const { data } = await api.get(`/esrs/qualitative/${std}/${y}`);
    setQual(data);
    setDrafts(data.disclosures || {});
  };

  useEffect(() => {
    if (tab === "E1") loadE1(year);
    else loadQual(tab, year);
  }, [tab, year]);

  const saveQual = async () => {
    try {
      await api.put("/esrs/qualitative", { standard: tab, year, disclosures: drafts });
      toast.success(t("esrsSaved"));
      loadQual(tab, year);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6" data-testid="esrs-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="max-w-2xl">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">CSRD · EFRAG</div>
          <h1 className="font-display text-3xl font-semibold mt-1">{t("esrsTitle")}</h1>
          <p className="text-muted-foreground text-sm mt-2">{t("esrsDesc")}</p>
        </div>
        <div className="flex gap-2 items-end">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">{t("year")}</Label>
            <Select value={String(year)} onValueChange={(v) => setYear(parseInt(v))}>
              <SelectTrigger className="w-[120px] font-mono-data" data-testid="esrs-year">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[0, 1, 2, 3, 4].map((n) => {
                  const y = new Date().getFullYear() - n;
                  return <SelectItem key={`esrs-y-${y}`} value={String(y)}>{y}</SelectItem>;
                })}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              const url = `${API_BASE}/esrs/e1/${year}/pdf`;
              const token = localStorage.getItem("cl_token") || "";
              const orgId = localStorage.getItem("cl_active_org") || "";
              fetch(url, {
                credentials: "include",
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  ...(orgId ? { "X-Org-Id": orgId } : {}),
                },
              })
                .then((r) => r.blob())
                .then((blob) => {
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = `esrs_e1_${year}.pdf`;
                  link.click();
                });
            }}
            data-testid="esrs-download-pdf"
          >
            <FileArrowDown size={16} className="mr-2" />
            {t("esrsDownloadPdf")}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const url = `${API_BASE}/esrs/e1/${year}/xbrl`;
              const token = localStorage.getItem("cl_token") || "";
              const orgId = localStorage.getItem("cl_active_org") || "";
              fetch(url, {
                credentials: "include",
                headers: {
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                  ...(orgId ? { "X-Org-Id": orgId } : {}),
                },
              })
                .then((r) => r.blob())
                .then((blob) => {
                  const link = document.createElement("a");
                  link.href = URL.createObjectURL(blob);
                  link.download = `esrs_e1_${year}.xbrl.xml`;
                  link.click();
                });
            }}
            data-testid="esrs-download-xbrl"
          >
            <FileArrowDown size={16} className="mr-2" />
            {t("esrsDownloadXbrl")}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border overflow-x-auto">
        {TABS.map((tabDef) => {
          const Icon = tabDef.icon;
          const active = tab === tabDef.key;
          return (
            <button
              key={tabDef.key}
              onClick={() => setTab(tabDef.key)}
              className={`flex items-center gap-2 px-4 py-2 text-sm border-b-2 transition-colors ${active ? "border-primary text-primary font-medium" : "border-transparent text-muted-foreground hover:text-foreground"}`}
              data-testid={`esrs-tab-${tabDef.key}`}
            >
              <Icon size={16} weight="duotone" />
              {tabDef.label}
            </button>
          );
        })}
      </div>

      {tab === "E1" && e1 && <E1Panel data={e1} t={t} />}
      {tab !== "E1" && qual && (
        <QualitativePanel
          standard={tab}
          qual={qual}
          drafts={drafts}
          setDrafts={setDrafts}
          onSave={saveQual}
          readonly={role !== "owner" && role !== "editor"}
          t={t}
        />
      )}
    </div>
  );
}

function E1Panel({ data, t }) {
  const e = data.e1_6_emissions;
  return (
    <div className="space-y-4" data-testid="esrs-e1-panel">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <Kpi label="Scope 1" value={e.scope_1_tco2e} suffix="tCO₂e" testid="e1-scope1" />
        <Kpi label="Scope 2" value={e.scope_2_location_based_tco2e} suffix="tCO₂e" testid="e1-scope2" />
        <Kpi label="Scope 3" value={e.scope_3_tco2e} suffix="tCO₂e" testid="e1-scope3" />
        <Kpi label={t("gross") || "Gross"} value={e.gross_total_tco2e} suffix="tCO₂e" accent testid="e1-total" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg flex items-center gap-2">
            <FileText size={18} weight="duotone" className="text-primary" />
            E1-6 · {t("esrsE1_6")}
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 font-mono-data">
          <Row label={t("esrsConsolidation")} value={e.consolidation_approach} />
          <Row label={t("esrsProtocol")} value={e.protocol} />
          <Row label={t("esrsReportingYear")} value={e.reporting_year} />
          <Row label={t("esrsRecords")} value={e.entry_count} />
          <Row label={t("esrsBiogenic")} value={`${e.biogenic_tco2e} tCO₂e`} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">E1-4 · {t("esrsE1_4")}</CardTitle>
        </CardHeader>
        <CardContent>
          {data.e1_4_targets.length === 0 ? (
            <div className="text-sm text-muted-foreground">{t("esrsNoTargets")}</div>
          ) : (
            <div className="space-y-2">
              {data.e1_4_targets.map((tg) => (
                <div key={tg.id} className="border border-border rounded-md p-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <CheckCircle size={16} weight="fill" className="text-primary" />
                    <span className="font-medium text-sm">{tg.name}</span>
                    <span className="text-[10px] uppercase font-mono-data bg-primary/10 text-primary px-2 py-0.5 rounded-sm">{tg.framework}</span>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono-data mt-1">
                    {tg.baseline_year} → {tg.target_year} · −{tg.reduction_pct}% · {tg.scopes.map((s) => `S${s}`).join(" + ")}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">E1-1 · {t("esrsE1_1")}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {data.e1_1_transition_plan.note}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">E1-5 · {t("esrsE1_5")}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <div className="text-muted-foreground">{data.e1_5_energy.note}</div>
          {data.e1_5_energy.energy_intensity_tco2e_per_meur != null && (
            <div className="font-mono-data">
              {t("esrsEnergyIntensity")}: <span className="font-medium text-foreground">{data.e1_5_energy.energy_intensity_tco2e_per_meur} tCO₂e / M€</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">E1-7 · {t("esrsE1_7")}</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          {data.e1_7_removals_offsets.note}
        </CardContent>
      </Card>
    </div>
  );
}

function QualitativePanel({ standard, qual, drafts, setDrafts, onSave, readonly, t }) {
  return (
    <div className="space-y-4" data-testid={`esrs-${standard.toLowerCase()}-panel`}>
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-lg">
            {standard} · {t(`esrsTitle_${standard}`)}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {qual.template.map((dp) => (
            <div key={dp.code} className="space-y-1.5" data-testid={`esrs-dp-${dp.code}`}>
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-xs font-mono-data uppercase text-primary bg-primary/10 px-2 py-0.5 rounded-sm">{dp.code}</span>
                <span className="text-sm font-medium">{dp.label}</span>
              </div>
              {dp.hint && <div className="text-xs text-muted-foreground italic">{dp.hint}</div>}
              <Textarea
                rows={3}
                value={drafts[dp.code] || ""}
                onChange={(e) => setDrafts({ ...drafts, [dp.code]: e.target.value })}
                placeholder={t("esrsWriteDisclosure")}
                disabled={readonly}
                data-testid={`esrs-input-${dp.code}`}
              />
            </div>
          ))}
          {!readonly && (
            <div className="flex justify-end">
              <Button onClick={onSave} data-testid="esrs-save">{t("save")}</Button>
            </div>
          )}
          {qual.updated_at && (
            <div className="text-xs text-muted-foreground font-mono-data text-right">{t("esrsLastSaved")}: {qual.updated_at.slice(0, 19).replace("T", " ")}</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const Kpi = ({ label, value, suffix, accent, testid }) => (
  <Card className={accent ? "border-primary/40 bg-primary/5" : undefined} data-testid={testid}>
    <CardContent className="p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-mono-data">{label}</div>
      <div className="font-display text-2xl font-semibold font-mono-data mt-1">
        {(value ?? 0).toLocaleString(undefined, { maximumFractionDigits: 3 })}
      </div>
      <div className="text-[10px] text-muted-foreground font-mono-data">{suffix}</div>
    </CardContent>
  </Card>
);

const Row = ({ label, value }) => (
  <div className="flex justify-between gap-4 border-b border-border/50 py-1.5 last:border-0">
    <span className="text-muted-foreground uppercase tracking-widest text-xs">{label}</span>
    <span className="text-right">{value}</span>
  </div>
);
